/**
 * brandKit.ts — the Phuket Cleanse VISUAL constitution, compiled into the app.
 *
 * This is the visual counterpart to the Copy Engine's creativeParadigm.ts: where
 * that module governs the WORDS, this governs how those words are set onto the
 * picture — the brand fonts, palette, and the finished-post templates. Per
 * CREATIVE_PARADIGM §3 the app never GENERATES imagery; it composites the brand's
 * own type/marks onto human-curated art. "The fonts live inside the creative."
 *
 * These colors + fonts are FIXED brand identity (not theme tokens) — they are
 * baked into every exported post regardless of the app's UI theme.
 *
 * Font families load via the Google Fonts <link> in index.html (Lora + Poppins).
 * The editor must await document.fonts.load(...) before drawing to canvas.
 */

export const BRAND = {
  name: 'Phuket Cleanse',
  // Mel's Meta/social brand kit.
  palette: {
    forest: '#14281E', // panels / scrim
    gold: '#C9A24B', // accents / RECODED mark / rules / tags
    cream: '#F4ECD8', // headlines on dark
    softWhite: '#F8F6F0', // soft white
  },
  fonts: {
    // Headline serif (the star) — headlines, dish names, hero RECODED wordmark.
    serif: 'Lora',
    // Clean sans — letter-spaced caps tags, fine print, the small RECODED mark.
    sans: 'Poppins',
  },
} as const;

/** The exact font weights the templates use — preloaded before any canvas draw. */
export const BRAND_FONT_SPECS = [
  "500 64px 'Lora'",
  "600 64px 'Lora'",
  "italic 500 64px 'Lora'",
  "500 40px 'Poppins'",
  "600 40px 'Poppins'",
] as const;

export interface Aspect {
  id: string;
  label: string;
  w: number;
  h: number;
}
/** The two Meta-feed ratios we export. 4:5 is the taller, higher-reach default. */
export const ASPECTS: Aspect[] = [
  { id: '4:5', label: 'Portrait 4:5', w: 1080, h: 1350 },
  { id: '1:1', label: 'Square 1:1', w: 1080, h: 1080 },
];

export interface TemplateField {
  key: string;
  label: string;
  default: string; // generic/blank — real copy is typed in, never sample text
  placeholder?: string; // instructional hint shown in the empty input
  multiline?: boolean;
  maxLen: number;
  optional?: boolean; // rendered only when non-empty
}
export interface Template {
  id: string;
  name: string;
  desc: string;
  fields: TemplateField[];
}

/**
 * Template registry. Each entry declares its editable fields; the pixel layout
 * lives in the matching renderer (renderTemplate) below. Add a template = add an
 * entry here + a branch in renderTemplate. Locked to the PC-approved looks, so
 * the path of least resistance is always the brand-correct path (paradigm §4).
 */
export const TEMPLATES: Template[] = [
  {
    id: 'dish-name-card',
    name: 'Name Card',
    desc: 'Lora headline + gold Poppins tag + RECODED™ corner mark, over the photo.',
    fields: [
      {
        key: 'tag',
        label: 'Tag (caps)',
        default: '',
        placeholder: 'Subtitle text',
        maxLen: 24,
        optional: true,
      },
      {
        key: 'headline',
        label: 'Headline',
        default: '',
        placeholder: 'Write text here',
        multiline: true,
        maxLen: 60,
      },
      {
        key: 'mark',
        label: 'Corner mark',
        default: 'RECODED™',
        placeholder: 'RECODED™',
        maxLen: 24,
        optional: true,
      },
    ],
  },
  {
    id: 'recoded-splash',
    name: 'RECODED™ Intro Splash',
    desc: 'Big Lora RECODED™ wordmark on a forest panel — the announcement slide.',
    fields: [
      {
        key: 'eyebrow',
        label: 'Eyebrow (caps)',
        default: '',
        placeholder: 'Eyebrow',
        maxLen: 24,
        optional: true,
      },
      {
        key: 'wordmark',
        label: 'Wordmark',
        default: 'RECODED™',
        placeholder: 'Wordmark',
        maxLen: 20,
      },
      {
        key: 'subhead',
        label: 'Subhead (italic)',
        default: '',
        placeholder: 'Supporting line',
        multiline: true,
        maxLen: 120,
        optional: true,
      },
    ],
  },
  {
    id: 'quote-whisper',
    name: 'Quote / Whisper',
    desc: 'A single Lora line over the image — the Luxury/Editorial whisper.',
    fields: [
      {
        key: 'quote',
        label: 'Line',
        default: '',
        placeholder: 'Write text here',
        multiline: true,
        maxLen: 90,
      },
      {
        key: 'mark',
        label: 'Corner mark',
        default: 'RECODED™',
        placeholder: 'RECODED™',
        maxLen: 24,
        optional: true,
      },
    ],
  },
];

export type TemplateValues = Record<string, string>;

// ── canvas helpers ───────────────────────────────────────────────────────────

const font = (family: string, weight: number, px: number, italic = false) =>
  `${italic ? 'italic ' : ''}${weight} ${px}px '${family}'`;

/** How the base image is positioned inside the frame (zoom + pan, in canvas px). */
export interface ImageTransform {
  zoom: number; // multiplier on the cover scale (>= 1)
  panX: number; // horizontal offset from centered, canvas px
  panY: number; // vertical offset from centered, canvas px
}

/** Largest pan (canvas px) that still keeps the frame fully covered, per axis. */
export function panBounds(iw: number, ih: number, w: number, h: number, zoom: number) {
  const scale = Math.max(w / iw, h / ih) * zoom;
  return { maxX: Math.max(0, (iw * scale - w) / 2), maxY: Math.max(0, (ih * scale - h) / 2) };
}

/**
 * Per-text-block layout override. Each block starts at its brand-correct default
 * (dx/dy 0, scale 1); the user can nudge it (dx/dy = fraction of canvas w/h) and
 * resize it (scale = multiplier on the default font size). Defaults keep the
 * path of least resistance brand-correct; overrides give hands-on control.
 */
export interface FieldLayout {
  dx: number;
  dy: number;
  scale: number; // font-size multiplier (corner handle)
  widthMul: number; // wrap-frame width multiplier (side handle) — controls line breaks
}
export type TemplateLayout = Record<string, FieldLayout>;
export const IDENTITY_LAYOUT: FieldLayout = { dx: 0, dy: 0, scale: 1, widthMul: 1 };
const L = (lay: TemplateLayout, key: string): FieldLayout => lay[key] ?? IDENTITY_LAYOUT;

/** A drawn text block's bounding box (canvas px, top-left) — for move/resize hit-testing. */
export interface FieldBox {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  align: 'left' | 'center' | 'right';
}

/** Cover-fit an image across the frame with optional zoom + pan (crop overflow). */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  iw: number,
  ih: number,
  w: number,
  h: number,
  t?: ImageTransform,
) {
  const zoom = t?.zoom ?? 1;
  const scale = Math.max(w / iw, h / ih) * zoom;
  const dw = iw * scale;
  const dh = ih * scale;
  const { maxX, maxY } = panBounds(iw, ih, w, h, zoom);
  const px = Math.max(-maxX, Math.min(maxX, t?.panX ?? 0));
  const py = Math.max(-maxY, Math.min(maxY, t?.panY ?? 0));
  ctx.drawImage(img, (w - dw) / 2 + px, (h - dh) / 2 + py, dw, dh);
}

/** Bottom-to-top gradient scrim so cream/gold type stays legible over any photo. */
function drawScrim(ctx: CanvasRenderingContext2D, w: number, h: number, fromPct: number) {
  const g = ctx.createLinearGradient(0, h * fromPct, 0, h);
  g.addColorStop(0, 'rgba(9,18,13,0)');
  g.addColorStop(1, 'rgba(9,18,13,0.82)'); // near-forest ink
  ctx.fillStyle = g;
  ctx.fillRect(0, h * fromPct, w, h * (1 - fromPct));
}

/** Greedy word-wrap for the current ctx.font. Respects explicit \n breaks. */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      out.push('');
      continue;
    }
    let line = words[0]!;
    for (let i = 1; i < words.length; i++) {
      const test = `${line} ${words[i]}`;
      if (ctx.measureText(test).width > maxWidth) {
        out.push(line);
        line = words[i]!;
      } else {
        line = test;
      }
    }
    out.push(line);
  }
  return out;
}

/** Total advance width of a string drawn with manual letter-spacing. */
function spacedWidth(ctx: CanvasRenderingContext2D, text: string, ls: number): number {
  let wsum = 0;
  for (const ch of text) wsum += ctx.measureText(ch).width + ls;
  return Math.max(0, wsum - ls);
}

/** Draw letter-spaced text (manual, cross-browser). `align`: left|right|center at x. */
function drawSpaced(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  ls: number,
  align: 'left' | 'right' | 'center',
) {
  const total = spacedWidth(ctx, text, ls);
  let cx = align === 'left' ? x : align === 'right' ? x - total : x - total / 2;
  ctx.textAlign = 'left';
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + ls;
  }
}

/**
 * Draw one positionable text block: applies its per-field layout override (move +
 * resize) on top of the passed brand-default anchor, then records its bounding box
 * so the editor can hit-test drags. Returns the box (or null if there's no text).
 * The default anchors are computed by each template at BASE size, so resizing a
 * block grows it from a stable origin.
 */
interface BlockOpts {
  key: string;
  text: string;
  family: string;
  weight: number;
  italic?: boolean;
  basePx: number;
  color: string;
  align: 'left' | 'center' | 'right';
  spaced?: boolean; // letter-spaced caps (tags/eyebrows) vs. kerned serif
  lsMul?: number; // letter-spacing as a fraction of font px
  wrapW: number; // base wrap width in canvas px (scaled with the font on resize)
  lineHeightMul: number;
  anchorX: number; // brand-default x (meaning depends on align)
  anchorY: number; // brand-default top y
  alpha: number;
}
function drawBlock(
  ctx: CanvasRenderingContext2D,
  o: BlockOpts,
  w: number,
  h: number,
  lay: FieldLayout,
  boxes?: FieldBox[],
): FieldBox | null {
  if (!o.text) return null;
  const px = Math.max(6, o.basePx * lay.scale);
  const effWrapW = o.wrapW * (lay.widthMul ?? 1);
  // Font size (scale) and frame width (widthMul) are independent controls: growing
  // the font wraps within the current frame; widening the frame reduces wrapping.
  ctx.font = font(o.family, o.weight, px, o.italic);
  const lines = wrapLines(ctx, o.text, effWrapW);
  const lh = px * o.lineHeightMul;
  const ls = px * (o.lsMul ?? 0);
  const blockW = Math.max(
    1,
    ...lines.map((ln) => (o.spaced ? spacedWidth(ctx, ln, ls) : ctx.measureText(ln).width)),
  );
  const ax = o.anchorX + lay.dx * w;
  const ay = o.anchorY + lay.dy * h;
  const left = o.align === 'left' ? ax : o.align === 'center' ? ax - blockW / 2 : ax - blockW;

  ctx.save();
  ctx.globalAlpha = o.alpha;
  ctx.fillStyle = o.color;
  ctx.textBaseline = 'top';
  let y = ay;
  for (const ln of lines) {
    if (o.spaced) {
      drawSpaced(ctx, ln, ax, y, ls, o.align);
    } else {
      ctx.textAlign = o.align;
      ctx.fillText(ln, ax, y);
    }
    y += lh;
  }
  ctx.restore();

  const box: FieldBox = {
    key: o.key,
    x: left,
    y: ay,
    w: blockW,
    h: lines.length * lh,
    align: o.align,
  };
  boxes?.push(box);
  return box;
}

// ── renderer ─────────────────────────────────────────────────────────────────

/**
 * Draw a template to a canvas context sized w×h with the background image already
 * decoded. Pure + synchronous — fonts MUST be loaded first (see the editor).
 */
/** Alpha for placeholder text drawn on the preview canvas (never exported). */
const PH_ALPHA = 1;

/**
 * A field's effective on-canvas text: the typed value, or (in preview mode) its
 * placeholder shown dimmed so the layout is visible before typing. `ph` flags a
 * placeholder so the caller can dim it; export mode (preview=false) skips it.
 */
function eff(
  value: string | undefined,
  placeholder: string | undefined,
  preview: boolean,
): { text: string; ph: boolean } {
  const t = (value ?? '').trim();
  if (t) return { text: t, ph: false };
  const p = (placeholder ?? '').trim();
  if (preview && p) return { text: p, ph: true };
  return { text: '', ph: false };
}

export interface RenderOpts {
  placeholders?: TemplateValues; // per-field placeholder text
  preview?: boolean; // true = show dimmed placeholders (editor); false = clean export
  layout?: TemplateLayout; // per-field move/resize overrides
  boxes?: FieldBox[]; // out-param: renderer fills with each block's hit box
}

export function renderTemplate(
  ctx: CanvasRenderingContext2D,
  templateId: string,
  values: TemplateValues,
  img: { source: CanvasImageSource; w: number; h: number } | null,
  w: number,
  h: number,
  transform?: ImageTransform,
  opts?: RenderOpts,
) {
  const ph = opts?.placeholders ?? {};
  const preview = opts?.preview ?? false;
  const lay = opts?.layout ?? {};
  const boxes = opts?.boxes;
  // Base: forest fill (shows through if no image yet), then the photo.
  ctx.fillStyle = BRAND.palette.forest;
  ctx.fillRect(0, 0, w, h);
  if (img) drawCover(ctx, img.source, img.w, img.h, w, h, transform);

  if (templateId === 'dish-name-card')
    return renderDishNameCard(ctx, values, w, h, ph, preview, lay, boxes);
  if (templateId === 'recoded-splash')
    return renderRecodedSplash(ctx, values, w, h, ph, preview, lay, boxes);
  if (templateId === 'quote-whisper')
    return renderQuoteWhisper(ctx, values, w, h, ph, preview, lay, boxes);
}

/** RECODED™ corner mark (Poppins, gold, spaced caps), bottom-right. Shared. */
function drawCornerMark(ctx: CanvasRenderingContext2D, mark: string, w: number, h: number) {
  const text = mark.trim();
  if (!text) return;
  const mSize = Math.round(w * 0.026);
  ctx.font = font(BRAND.fonts.sans, 500, mSize);
  ctx.fillStyle = BRAND.palette.gold;
  ctx.textBaseline = 'alphabetic';
  drawSpaced(ctx, text.toUpperCase(), w - w * 0.06, h - h * 0.05, mSize * 0.18, 'right');
}

/** Largest px (≤ startPx) at which `text` fits `maxWidth` on one line. */
function fitSingleLine(
  ctx: CanvasRenderingContext2D,
  text: string,
  family: string,
  weight: number,
  startPx: number,
  maxWidth: number,
): number {
  let px = startPx;
  ctx.font = font(family, weight, px);
  while (px > 12 && ctx.measureText(text).width > maxWidth) {
    px -= 2;
    ctx.font = font(family, weight, px);
  }
  return px;
}

function renderDishNameCard(
  ctx: CanvasRenderingContext2D,
  v: TemplateValues,
  w: number,
  h: number,
  ph: TemplateValues,
  preview: boolean,
  lay: TemplateLayout,
  boxes?: FieldBox[],
) {
  drawScrim(ctx, w, h, 0.42);

  const padX = w * 0.07;
  const tagF = eff(v.tag, ph.tag, preview);
  const headF = eff(v.headline, ph.headline, preview);
  const markF = eff(v.mark, ph.mark, preview);
  const tag = tagF.text.toUpperCase();
  const headline = headF.text;

  // Brand-default cluster geometry, computed at BASE size (overrides move/resize
  // each block from these stable anchors).
  const hSize = Math.round(w * 0.076);
  const hLineH = hSize * 1.12;
  ctx.font = font(BRAND.fonts.serif, 500, hSize);
  const hLinesBase = headline ? wrapLines(ctx, headline, w * 0.86) : [];

  const tagSize = Math.round(w * 0.03);
  const tagGap = tagSize * 1.5;

  // Bottom-anchor the whole cluster so 1- or 2-line headlines both sit right.
  const blockBottom = h - h * 0.075;
  const clusterH = (tag ? tagSize + tagGap : 0) + hLinesBase.length * hLineH;
  const y0 = blockBottom - clusterH;
  const tagY = y0;
  const headY = y0 + (tag ? tagSize + tagGap : 0);

  drawBlock(
    ctx,
    {
      key: 'tag',
      text: tag,
      family: BRAND.fonts.sans,
      weight: 500,
      basePx: tagSize,
      color: BRAND.palette.gold,
      align: 'left',
      spaced: true,
      lsMul: 0.22,
      wrapW: w,
      lineHeightMul: 1.4,
      anchorX: padX,
      anchorY: tagY,
      alpha: tagF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'tag'),
    boxes,
  );

  drawBlock(
    ctx,
    {
      key: 'headline',
      text: headline,
      family: BRAND.fonts.serif,
      weight: 500,
      basePx: hSize,
      color: BRAND.palette.cream,
      align: 'left',
      wrapW: w * 0.86,
      lineHeightMul: 1.12,
      anchorX: padX,
      anchorY: headY,
      alpha: headF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'headline'),
    boxes,
  );

  if (markF.text) {
    ctx.globalAlpha = markF.ph ? PH_ALPHA : 1;
    drawCornerMark(ctx, markF.text, w, h);
    ctx.globalAlpha = 1;
  }
}

function renderRecodedSplash(
  ctx: CanvasRenderingContext2D,
  v: TemplateValues,
  w: number,
  h: number,
  ph: TemplateValues,
  preview: boolean,
  lay: TemplateLayout,
  boxes?: FieldBox[],
) {
  // Heavy forest panel over whatever's behind (photo optional) — brand slide.
  ctx.fillStyle = 'rgba(20,40,30,0.86)';
  ctx.fillRect(0, 0, w, h);

  const ebF = eff(v.eyebrow, ph.eyebrow, preview);
  const wmF = eff(v.wordmark, ph.wordmark, preview);
  const subF = eff(v.subhead, ph.subhead, preview);
  const eyebrow = ebF.text.toUpperCase();
  const wordmark = wmF.text;
  const subhead = subF.text;
  const cx = w / 2;

  // Brand-default centered cluster geometry, measured at BASE size.
  const wmSize = fitSingleLine(ctx, wordmark || ' ', BRAND.fonts.serif, 600, w * 0.14, w * 0.82);
  const ebSize = Math.round(w * 0.028);
  const subSize = Math.round(w * 0.036);
  ctx.font = font(BRAND.fonts.serif, 500, subSize, true);
  const subLinesBase = subhead ? wrapLines(ctx, subhead, w * 0.68) : [];

  const ruleGap = h * 0.03;
  const blockH =
    (eyebrow ? ebSize + h * 0.028 : 0) +
    (wordmark ? wmSize + ruleGap * 2 : 0) +
    (subLinesBase.length ? subLinesBase.length * subSize * 1.3 : 0);
  const top = (h - blockH) / 2;
  const ebY = top;
  const wmY = top + (eyebrow ? ebSize + h * 0.028 : 0);
  const subY = wmY + (wordmark ? wmSize + ruleGap * 2 : 0);

  drawBlock(
    ctx,
    {
      key: 'eyebrow',
      text: eyebrow,
      family: BRAND.fonts.sans,
      weight: 500,
      basePx: ebSize,
      color: BRAND.palette.gold,
      align: 'center',
      spaced: true,
      lsMul: 0.28,
      wrapW: w,
      lineHeightMul: 1.4,
      anchorX: cx,
      anchorY: ebY,
      alpha: ebF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'eyebrow'),
    boxes,
  );

  const wmBox = drawBlock(
    ctx,
    {
      key: 'wordmark',
      text: wordmark,
      family: BRAND.fonts.serif,
      weight: 600,
      basePx: wmSize,
      color: BRAND.palette.gold,
      align: 'center',
      wrapW: w * 0.82,
      lineHeightMul: 1.0,
      anchorX: cx,
      anchorY: wmY,
      alpha: wmF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'wordmark'),
    boxes,
  );

  // Gold hairline rule, tucked just under the wordmark (follows it when moved).
  if (wmBox) {
    ctx.save();
    ctx.globalAlpha = wmF.ph ? PH_ALPHA : 1;
    ctx.strokeStyle = BRAND.palette.gold;
    ctx.lineWidth = Math.max(1, w * 0.0015);
    const ruleY = wmBox.y + wmBox.h + ruleGap;
    const rcx = wmBox.x + wmBox.w / 2;
    ctx.beginPath();
    ctx.moveTo(rcx - w * 0.06, ruleY);
    ctx.lineTo(rcx + w * 0.06, ruleY);
    ctx.stroke();
    ctx.restore();
  }

  drawBlock(
    ctx,
    {
      key: 'subhead',
      text: subhead,
      family: BRAND.fonts.serif,
      weight: 500,
      italic: true,
      basePx: subSize,
      color: BRAND.palette.cream,
      align: 'center',
      wrapW: w * 0.68,
      lineHeightMul: 1.3,
      anchorX: cx,
      anchorY: subY,
      alpha: subF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'subhead'),
    boxes,
  );
}

function renderQuoteWhisper(
  ctx: CanvasRenderingContext2D,
  v: TemplateValues,
  w: number,
  h: number,
  ph: TemplateValues,
  preview: boolean,
  lay: TemplateLayout,
  boxes?: FieldBox[],
) {
  // Gentle full overlay so a single cream line reads anywhere on the photo.
  ctx.fillStyle = 'rgba(9,18,13,0.34)';
  ctx.fillRect(0, 0, w, h);
  drawScrim(ctx, w, h, 0.55);

  const quoteF = eff(v.quote, ph.quote, preview);
  const markF = eff(v.mark, ph.mark, preview);
  const qSize = Math.round(w * 0.072);
  const qLineH = qSize * 1.16;

  // Brand default: vertically centered on the frame (at base size).
  ctx.font = font(BRAND.fonts.serif, 500, qSize);
  const linesBase = quoteF.text ? wrapLines(ctx, quoteF.text, w * 0.78) : [];
  const qY = (h - linesBase.length * qLineH) / 2;

  drawBlock(
    ctx,
    {
      key: 'quote',
      text: quoteF.text,
      family: BRAND.fonts.serif,
      weight: 500,
      basePx: qSize,
      color: BRAND.palette.cream,
      align: 'center',
      wrapW: w * 0.78,
      lineHeightMul: 1.16,
      anchorX: w / 2,
      anchorY: qY,
      alpha: quoteF.ph ? PH_ALPHA : 1,
    },
    w,
    h,
    L(lay, 'quote'),
    boxes,
  );

  if (markF.text) {
    ctx.globalAlpha = markF.ph ? PH_ALPHA : 1;
    drawCornerMark(ctx, markF.text, w, h);
    ctx.globalAlpha = 1;
  }
}
